// The Boot server page's first part (spec/25 §16.1): the venue's boot server — its status in one sentence, then its version,
// licence state and RAM cache, its drives and free space — and the way to pair it. A venue has one boot server. Its health
// and settings follow on the same page (`ServerPage`). Pairing is a code to type into the Pairing box on the server's status page, shown
// once in a centred dialog; renaming and unpairing are dialogs too. `boot:manage` pairs, renames and unpairs.
//
// Software: the boot server's own updates. It downloads and checks a new version by itself, then waits: Update now (a
// centred dialog, `boot:manage`) installs it. With PCs booted from the server the dialog says how many and offers to
// update anyway; the server itself refuses without that. The last update's result shows here too.

import type { BootDisk, BootServer, BootServerUpdate, BootServerUpdateState } from '@clavian/api-client';
import { useTranslation, useVenueFormatter } from '@clavian/i18n/react';
import { Badge, Button, Dialog, EmptyState, ProgressBar, Table, TextField, useToast, type TableColumn } from '@clavian/ui-web';
import type { UseQueryResult } from '@tanstack/react-query';
import { useState, type FormEvent, type ReactNode } from 'react';

import { problemDetail } from '../api/problems';
import { LoadFailure } from '../shell/LoadFailure';
import { Icon } from '../shell/nav-icons';
import { Facts, Notice, Section, TableSkeleton } from '../ui';
import type { BootAccess } from './BootPage';
import { formatBytes, isDiskLow, needsLicenceAttention, usedPercent } from './format';
import { useCreatePairingCode, useRenameBootServer, useUnpairBootServer, useUpdateBootServerSoftware, type BootScope } from './queries';

export function ServersTab({ scope, servers, access }: { readonly scope: BootScope; readonly servers: UseQueryResult<BootServer[], unknown>; readonly access: BootAccess }): ReactNode {
  const { t } = useTranslation('console');
  const [pairing, setPairing] = useState(false);

  if (servers.data === undefined) {
    return servers.isError ? <LoadFailure error={servers.error} message={t('boot.unavailable')} onRetry={() => void servers.refetch()} /> : <TableSkeleton rows={4} label={t('boot.loading')} />;
  }
  const server = servers.data[0];
  const pairingDialog = <PairingDialog scope={scope} open={pairing} onClose={() => setPairing(false)} />;

  if (server === undefined || server.status === 'pairing') {
    return (
      <>
        <EmptyState
          icon={<Icon name="boot" />}
          title={t('boot.servers.empty.title')}
          description={t('boot.servers.empty.body')}
          action={
            access.canManage ? (
              <Button variant="primary" onClick={() => setPairing(true)}>
                {t('boot.servers.pair')}
              </Button>
            ) : undefined
          }
        />
        {server === undefined ? null : <p className="console-muted">{t('boot.servers.waiting', { name: server.name })}</p>}
        {pairingDialog}
      </>
    );
  }
  return (
    <>
      <ServerCard scope={scope} server={server} access={access} onPair={() => setPairing(true)} />
      {pairingDialog}
    </>
  );
}

const STATUS_TONE = { online: 'ok', offline: 'warn', pairing: 'info' } as const;

/** The status card's one sentence: online (and how many PCs start from it), not reporting, or waiting to be paired. */
export function statusSentence(server: BootServer | undefined, t: (key: string, options?: Record<string, unknown>) => string, relative: (iso: string) => string): string {
  if (server === undefined) return t('boot.servers.summary.none');
  if (server.status === 'pairing') return t('boot.servers.summary.pairing');
  if (server.status === 'offline') return t('boot.servers.summary.offline', { when: server.lastSeenAt === null ? t('boot.servers.never') : relative(server.lastSeenAt) });
  if (server.licenceState === 'expired') return t('boot.servers.summary.licenceExpired');
  if (server.licenceState === 'grace') return t('boot.servers.summary.licenceGrace');
  const started = server.update?.bootedPcs ?? 0;
  return started > 0 ? t('boot.servers.summary.onlineBooted', { count: started }) : t('boot.servers.summary.online');
}
const LICENCE_TONE = { valid: 'ok', grace: 'warn', expired: 'danger' } as const;

function ServerCard({ scope, server, access, onPair }: { readonly scope: BootScope; readonly server: BootServer; readonly access: BootAccess; readonly onPair: () => void }) {
  const { t } = useTranslation('console');
  const format = useVenueFormatter();
  const [renaming, setRenaming] = useState(false);
  const [unpairing, setUnpairing] = useState(false);

  const columns: readonly TableColumn<BootDisk>[] = [
    { key: 'role', header: t('boot.servers.disks.role'), cell: (disk) => t(`boot.servers.disks.roles.${disk.role}`) },
    { key: 'path', header: t('boot.servers.disks.path'), cell: (disk) => <span className="clav-figure">{disk.path}</span> },
    {
      key: 'use',
      header: t('boot.servers.disks.use'),
      cell: (disk) => (
        <div className="console-boot__use">
          <ProgressBar value={usedPercent(disk) / 100} label={t('boot.servers.disks.useLabel', { role: t(`boot.servers.disks.roles.${disk.role}`) })} tone={isDiskLow(disk) ? 'warn' : 'accent'} size="sm" />
          {isDiskLow(disk) ? <Badge tone="warn">{t('boot.servers.disks.low')}</Badge> : null}
        </div>
      ),
    },
    { key: 'free', header: t('boot.servers.disks.free'), numeric: true, cell: (disk) => formatBytes(disk.freeBytes) },
    { key: 'total', header: t('boot.servers.disks.total'), numeric: true, cell: (disk) => formatBytes(disk.totalBytes) },
  ];

  return (
    <div className="console-boot__stack">
      <Section
        title={server.name}
        actions={
          access.canManage ? (
            <>
              <Button variant="secondary" onClick={() => setRenaming(true)}>
                {t('boot.servers.rename')}
              </Button>
              <Button variant="secondary" onClick={onPair}>
                {t('boot.servers.pairAgain')}
              </Button>
              <Button variant="secondary" onClick={() => setUnpairing(true)}>
                {t('boot.servers.unpair')}
              </Button>
            </>
          ) : undefined
        }
      >
        <p className="console-boot__summary">{statusSentence(server, t, (iso) => format.relative(iso))}</p>
        {server.status === 'offline' ? (
          <Notice tone="warn" role="status">
            {t('boot.servers.offlineNote')}
          </Notice>
        ) : null}
        {server.licenceState === 'grace' ? (
          <Notice tone="warn" role="status" title={t('boot.servers.licence.graceTitle')}>
            {t('boot.servers.licence.graceBody')}
          </Notice>
        ) : null}
        {server.licenceState === 'expired' ? (
          <Notice tone="danger" role="alert" title={t('boot.servers.licence.expiredTitle')}>
            {t('boot.servers.licence.expiredBody')}
          </Notice>
        ) : null}
        <Facts
          columns={3}
          items={[
            { key: 'status', label: t('boot.servers.status'), value: <Badge tone={STATUS_TONE[server.status]} dot>{t(`boot.servers.statuses.${server.status}`)}</Badge> },
            { key: 'version', label: t('boot.servers.version'), value: server.version ?? t('boot.servers.unknown') },
            { key: 'seen', label: t('boot.servers.lastSeen'), value: server.lastSeenAt === null ? t('boot.servers.never') : format.relative(server.lastSeenAt) },
            {
              key: 'licence',
              label: t('boot.servers.licence.label'),
              value:
                server.licenceState === null ? (
                  t('boot.servers.unknown')
                ) : (
                  <Badge tone={LICENCE_TONE[server.licenceState]} dot>
                    {t(`boot.servers.licence.states.${server.licenceState}`)}
                  </Badge>
                ),
            },
            { key: 'ram', label: t('boot.servers.ramCache'), value: server.ramCacheBytes === null ? t('boot.servers.unknown') : formatBytes(server.ramCacheBytes) },
            { key: 'paired', label: t('boot.servers.pairedOn'), value: server.pairedAt === null ? t('boot.servers.unknown') : format.date(server.pairedAt, 'medium') },
          ]}
        />
        {needsLicenceAttention(server) ? null : <p className="console-muted">{t('boot.servers.licence.okNote')}</p>}
      </Section>
      <SoftwareSection scope={scope} server={server} access={access} />
      <Section title={t('boot.servers.disks.title')} description={t('boot.servers.disks.intro')} body="rows">
        {server.disks.length === 0 ? (
          <p className="console-muted">{t('boot.servers.disks.none')}</p>
        ) : (
          <Table caption={t('boot.servers.disks.title')} captionHidden columns={columns} rows={server.disks} rowKey={(disk) => `${disk.role}:${disk.path}`} />
        )}
      </Section>
      <RenameDialog scope={scope} server={server} open={renaming} onClose={() => setRenaming(false)} />
      <UnpairDialog scope={scope} server={server} open={unpairing} onClose={() => setUnpairing(false)} />
    </div>
  );
}

// -- Software: the boot server's own updates -------------------------------------------------------------

const UPDATE_TONE: Record<BootServerUpdateState, 'ok' | 'warn' | 'danger' | 'info' | 'accent' | 'neutral'> = {
  none: 'ok',
  downloading: 'info',
  available: 'accent',
  installing: 'info',
  installed: 'ok',
  failed: 'danger',
  rolled_back: 'warn',
};

/** Whether Update now can be pressed: an update waits, nothing is being done, and the member may manage the server. */
function canUpdate(update: BootServerUpdate | null): update is BootServerUpdate & { availableVersion: string } {
  return update !== null && update.availableVersion !== null && update.state !== 'installing' && update.state !== 'downloading';
}

function SoftwareSection({ scope, server, access }: { readonly scope: BootScope; readonly server: BootServer; readonly access: BootAccess }) {
  const { t } = useTranslation('console');
  const format = useVenueFormatter();
  const [updating, setUpdating] = useState(false);
  const update = server.update;
  const online = server.status === 'online';

  const lastUpdate = update?.lastUpdate ?? null;
  return (
    <Section
      title={t('boot.servers.update.title')}
      description={t('boot.servers.update.intro')}
      actions={
        access.canManage && canUpdate(update) ? (
          <Button variant="primary" disabled={!online} disabledReason={t('boot.servers.update.offline')} onClick={() => setUpdating(true)}>
            {t('boot.servers.update.action')}
          </Button>
        ) : undefined
      }
    >
      {update === null ? (
        <p className="console-muted">{t('boot.servers.update.notReported')}</p>
      ) : (
        <>
          {update.state === 'available' && update.availableVersion !== null ? (
            <Notice tone="info" role="status" title={t('boot.servers.update.available', { version: update.availableVersion })}>
              {t('boot.servers.update.availableBody')}
            </Notice>
          ) : null}
          {update.state === 'installing' ? (
            <Notice tone="info" role="status">
              {t('boot.servers.update.installingBody', { version: update.availableVersion ?? '' })}
            </Notice>
          ) : null}
          {update.detail !== null && update.state !== 'installing' ? (
            <Notice tone={update.state === 'failed' ? 'danger' : 'warn'} role="status">
              {update.detail}
            </Notice>
          ) : null}
          <Facts
            columns={3}
            items={[
              { key: 'installed', label: t('boot.servers.update.installed'), value: update.installedVersion ?? t('boot.servers.unknown') },
              {
                key: 'state',
                label: t('boot.servers.update.state'),
                value: (
                  <Badge tone={UPDATE_TONE[update.state]} dot>
                    {t(`boot.servers.update.states.${update.state}`)}
                  </Badge>
                ),
              },
              { key: 'booted', label: t('boot.servers.update.bootedPcs'), value: update.bootedPcs === null ? t('boot.servers.unknown') : format.number(update.bootedPcs) },
              {
                key: 'last',
                label: t('boot.servers.update.last'),
                value:
                  lastUpdate === null
                    ? t('boot.servers.update.never')
                    : t(`boot.servers.update.lastResult.${lastUpdate.outcome}`, { from: lastUpdate.fromVersion, to: lastUpdate.toVersion, when: format.relative(lastUpdate.at) }),
              },
            ]}
          />
        </>
      )}
      {canUpdate(update) ? <UpdateDialog scope={scope} server={server} update={update} open={updating} onClose={() => setUpdating(false)} /> : null}
    </Section>
  );
}

function UpdateDialog({
  scope,
  server,
  update,
  open,
  onClose,
}: {
  readonly scope: BootScope;
  readonly server: BootServer;
  readonly update: BootServerUpdate & { availableVersion: string };
  readonly open: boolean;
  readonly onClose: () => void;
}) {
  const { t } = useTranslation('console');
  const { show } = useToast();
  const send = useUpdateBootServerSoftware(scope);
  const [failure, setFailure] = useState<string | undefined>(undefined);
  const booted = update.bootedPcs ?? 0;

  function close(): void {
    setFailure(undefined);
    send.reset();
    onClose();
  }

  function confirm(force: boolean): void {
    setFailure(undefined);
    send.mutate(
      { bootServerId: server.id, force },
      {
        onSuccess: () => {
          close();
          show({ tone: 'ok', title: t('boot.servers.update.dialog.sent') });
        },
        onError: (caught) => setFailure(problemDetail(caught) ?? t('boot.servers.update.dialog.failed')),
      },
    );
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => (next ? undefined : close())}
      size="sm"
      title={t('boot.servers.update.dialog.title', { version: update.availableVersion })}
      description={t('boot.servers.update.dialog.body')}
    >
      <div className="console-form">
        {booted > 0 ? (
          <Notice tone="warn" role="alert" title={t('boot.servers.update.dialog.bootedTitle', { count: booted })}>
            {t('boot.servers.update.dialog.bootedBody')}
          </Notice>
        ) : null}
        {failure === undefined ? null : (
          <Notice tone="danger" role="alert">
            {failure}
          </Notice>
        )}
        <div className="console-form__actions">
          <Button variant="ghost" onClick={close}>
            {t('common.cancel')}
          </Button>
          {booted > 0 ? (
            <Button variant="danger" busy={send.isPending} onClick={() => confirm(true)}>
              {t('boot.servers.update.dialog.confirmAnyway')}
            </Button>
          ) : (
            <Button variant="primary" busy={send.isPending} onClick={() => confirm(false)}>
              {t('boot.servers.update.dialog.confirm')}
            </Button>
          )}
        </div>
      </div>
    </Dialog>
  );
}

// -- Pairing ----------------------------------------------------------------------------------------

function PairingDialog({ scope, open, onClose }: { readonly scope: BootScope; readonly open: boolean; readonly onClose: () => void }) {
  const { t } = useTranslation('console');
  const format = useVenueFormatter();
  const create = useCreatePairingCode(scope);
  const [name, setName] = useState('');
  const [failure, setFailure] = useState<string | undefined>(undefined);
  const code = create.data;

  function close(): void {
    create.reset();
    setFailure(undefined);
    onClose();
  }

  function submit(event: FormEvent): void {
    event.preventDefault();
    setFailure(undefined);
    const trimmed = name.trim();
    create.mutate(trimmed === '' ? {} : { name: trimmed }, { onError: (caught) => setFailure(problemDetail(caught) ?? t('boot.servers.pairing.failed')) });
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => (next ? undefined : close())}
      size="sm"
      title={t('boot.servers.pairing.title')}
      description={code === undefined ? t('boot.servers.pairing.intro') : t('boot.servers.pairing.codeIntro')}
    >
      {code === undefined ? (
        <form className="console-form" noValidate aria-label={t('boot.servers.pairing.title')} onSubmit={submit}>
          {failure === undefined ? null : (
            <Notice tone="danger" role="alert">
              {failure}
            </Notice>
          )}
          <TextField label={t('boot.servers.pairing.name')} description={t('boot.servers.pairing.nameHint')} showOptional autoComplete="off" value={name} onChange={(event) => setName(event.target.value)} />
          <div className="console-form__actions">
            <Button variant="ghost" onClick={close}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" variant="primary" busy={create.isPending}>
              {t('boot.servers.pairing.get')}
            </Button>
          </div>
        </form>
      ) : (
        <div className="console-form">
          <p className="console-boot__code" role="status" aria-label={t('boot.servers.pairing.codeLabel')}>
            {code.code}
          </p>
          <ol className="console-boot__steps">
            <li>{t('boot.servers.pairing.step1')}</li>
            <li>{t('boot.servers.pairing.step2')}</li>
            <li>{t('boot.servers.pairing.step3')}</li>
          </ol>
          <p className="console-muted">{t('boot.servers.pairing.expires', { time: format.time(code.expiresAt, 'short') })}</p>
          <div className="console-form__actions">
            <Button variant="primary" onClick={close}>
              {t('boot.servers.pairing.done')}
            </Button>
          </div>
        </div>
      )}
    </Dialog>
  );
}

function RenameDialog({ scope, server, open, onClose }: { readonly scope: BootScope; readonly server: BootServer; readonly open: boolean; readonly onClose: () => void }) {
  const { t } = useTranslation('console');
  const { show } = useToast();
  const rename = useRenameBootServer(scope);
  const [name, setName] = useState(server.name);
  const [failure, setFailure] = useState<string | undefined>(undefined);

  function submit(event: FormEvent): void {
    event.preventDefault();
    const trimmed = name.trim();
    if (trimmed === '') {
      setFailure(t('boot.servers.renameDialog.required'));
      return;
    }
    rename.mutate(
      { bootServerId: server.id, name: trimmed },
      {
        onSuccess: () => {
          onClose();
          show({ tone: 'ok', title: t('boot.servers.renameDialog.saved') });
        },
        onError: (caught) => setFailure(problemDetail(caught) ?? t('boot.servers.renameDialog.failed')),
      },
    );
  }

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? undefined : onClose())} size="sm" title={t('boot.servers.renameDialog.title')}>
      {open ? (
        <form className="console-form" noValidate aria-label={t('boot.servers.renameDialog.title')} onSubmit={submit}>
          <TextField label={t('boot.servers.renameDialog.name')} required autoComplete="off" value={name} error={failure} onChange={(event) => setName(event.target.value)} />
          <div className="console-form__actions">
            <Button variant="ghost" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" variant="primary" busy={rename.isPending}>
              {t('common.save')}
            </Button>
          </div>
        </form>
      ) : null}
    </Dialog>
  );
}

function UnpairDialog({ scope, server, open, onClose }: { readonly scope: BootScope; readonly server: BootServer; readonly open: boolean; readonly onClose: () => void }) {
  const { t } = useTranslation('console');
  const { show } = useToast();
  const unpair = useUnpairBootServer(scope);
  const [failure, setFailure] = useState<string | undefined>(undefined);

  function confirm(): void {
    setFailure(undefined);
    unpair.mutate(server.id, {
      onSuccess: () => {
        onClose();
        show({ tone: 'ok', title: t('boot.servers.unpairDialog.done') });
      },
      onError: (caught) => setFailure(problemDetail(caught) ?? t('boot.servers.unpairDialog.failed')),
    });
  }

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? undefined : onClose())} size="sm" title={t('boot.servers.unpairDialog.title', { name: server.name })} description={t('boot.servers.unpairDialog.body')}>
      <div className="console-form">
        {failure === undefined ? null : (
          <Notice tone="danger" role="alert">
            {failure}
          </Notice>
        )}
        <div className="console-form__actions">
          <Button variant="ghost" onClick={onClose}>
            {t('boot.servers.unpairDialog.keep')}
          </Button>
          <Button variant="danger" busy={unpair.isPending} onClick={confirm}>
            {t('boot.servers.unpairDialog.confirm')}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
